import { useTranslation } from "@i18n";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import IconComponent from "../../cubeComponent/icons";
import { getUrlForImage } from "../../cubeComponent/constants";
import { useSelector } from "react-redux";
import { getImageUrl } from "@Utils";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";
import { groupByBucket } from "./dateBuckets";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { NotificationEntry } from "./useAllNotification";

function ImageWithFallback({ imageUrl, defaultImage }: { imageUrl?: string; defaultImage?: string }) {
  const [imgSrc, setImgSrc] = useState<string | undefined>(imageUrl);
  const handleError = () => {
    setImgSrc(defaultImage);
  };

  return (
    <img className="w-full h-full rounded-full object-cover" src={imgSrc} onError={handleError} alt="projectImage" />
  );
}

interface NotificationItemProps {
  notification: NotificationEntry;
  layout: RootState["layout"];
  /** Passed down rather than re-hooked so every row shares the parent's translator instance. */
  t: ReturnType<typeof useTranslation>["t"];
  isFirst: boolean;
  isHovered: boolean;
  isSelected: boolean;
  iconTypeMap: { [key: string]: string };
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  onToggleSelect: (id: ObjectIdString) => void;
  onDismiss?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

function NotificationItem({
  notification,
  layout,
  t,
  isFirst,
  isHovered,
  isSelected,
  iconTypeMap,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onToggleSelect,
  onDismiss,
}: NotificationItemProps) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = textRef.current;
    // Only a clamped node answers whether the text overflows: while `expanded` lifts the clamp,
    // scrollHeight === clientHeight, so measuring then would latch `overflows` false and the chevron
    // would vanish on collapse, leaving the row truncated with no way to expand it. The chevron stays
    // visible while expanded because the render below ORs `expanded` in.
    if (!node || expanded) {
      return;
    }
    // The observer — not the dep list — is what re-measures after a container resize (window narrowed,
    // sidebar collapsed): re-wrapping the text changes no dep. It also delivers one callback on
    // `observe()`, which is the initial measurement.
    // Clamped height is quantised to whole lines, so a text change that keeps the same clamped height
    // fires no resize; `notification.text` stays in the deps to cover that (it is a fresh JSX element
    // on every list reload — getNotificationParams builds a <Trans/> — so a reload re-measures too).
    const observer = new ResizeObserver(() => {
      setOverflows(node.scrollHeight > node.clientHeight + 1);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [notification.text, expanded]);

  let cursorClass = "";
  if (isHovered) {
    cursorClass = notification.redirectUrl ? "cursor-pointer" : "cursor-not-allowed";
  }

  return (
    <div
      className={`flex items-start gap-6 px-6 py-6 transition-colors ${isFirst ? "" : "border-t border-[#c6d8ec]"} ${
        isSelected ? "bg-[#e5f6ff]" : isHovered ? "bg-[#f7faff]" : ""
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(notification.id);
        }}
        title={isSelected ? t("Deselect") : t("Select")}
        className={`relative shrink-0 size-12 flex items-center justify-center overflow-hidden rounded-full cursor-pointer transition-transform duration-150 ease-out active:scale-90 ${
          isSelected ? "bg-[#caecff]" : "border-2 border-[#6bbbe3] bg-blue-50"
        }`}
      >
        {isSelected ? (
          <CheckBoxComponent checked sx={{ p: 0, pointerEvents: "none" }} />
        ) : notification.type === "Project" ? (
          <ImageWithFallback
            imageUrl={getUrlForImage({ id: `${notification.projectId}` }, "projectImage")}
            defaultImage={`${getImageUrl()! + layout.PROJECT_LOGO!}`}
          />
        ) : (
          <IconComponent
            className="w-6 h-6"
            // Every notification carries a `type`; the fallback covers an unmapped one, not an absent one.
            name={iconTypeMap[notification.type!] || "DefaultIcon"}
            width="1.5rem"
            height="1.5rem"
          />
        )}
      </button>

      <div className="flex flex-1 min-w-0 items-center gap-4 self-stretch">
        <button
          type="button"
          onClick={onClick}
          className={`flex flex-col gap-2 flex-1 min-w-0 text-start ${cursorClass}`}
          key={notification.id}
        >
          <div
            ref={textRef}
            className="text-sm text-[#192048]"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: expanded ? "unset" : 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            {notification.text}
          </div>
          <Text className="text-xs text-[#666a84]">{notification.time}</Text>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          {(overflows || expanded) && (
            <ButtonComponent
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              className="shrink-0"
            >
              <IconComponent
                name="arrowDownSm"
                width="1rem"
                height="1rem"
                className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </ButtonComponent>
          )}
          {isHovered && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss?.(e);
              }}
              className="shrink-0 flex items-center justify-center p-1 rounded-xs cursor-pointer hover:bg-[#e8f1fc]"
              title={t("Dismiss")}
            >
              <IconComponent name="Cancel" width="1rem" height="1rem" className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface NotificationCardProps {
  notifications: NotificationEntry[];
  iconTypeMap: { [key: string]: string };
  dismissNotification: (id: ObjectIdString) => void;
  closeNotificationModal: () => void;
  onMarkGroupRead: (bucketKey: string, ids: ObjectIdString[]) => void;
  selectedIds: Set<ObjectIdString>;
  onToggleSelect: (id: ObjectIdString) => void;
}

function NotificationCard({
  notifications,
  iconTypeMap,
  dismissNotification,
  closeNotificationModal,
  onMarkGroupRead,
  selectedIds,
  onToggleSelect,
}: NotificationCardProps) {
  const { t } = useTranslation();
  const layout = useSelector((state: RootState) => state.layout);
  const navigate = useNavigate();

  const [hoveredNotification, setHoveredNotification] = useState<string | null>(null);

  // TODO(exactOptionalPropertyTypes): `groupByBucket` (Notification/dateBuckets.ts:108) constrains
  // its rows to `{ createdDate?: number | string }` — a key that may be ABSENT — while
  // `NotificationEntry.createdDate` (inferred from `getNotificationParams`) is `number | undefined`,
  // so the constraint no longer holds and the three call sites below lose their row type with it.
  // The fix is on that constraint (`createdDate?: number | string | undefined`), which is neither
  // this file nor any file this change owns.
  const bucketedGroups = useMemo(() => groupByBucket(notifications, t), [notifications, t]);

  const handleMouseEnter = (notification: NotificationEntry) => {
    setHoveredNotification(notification.id);
  };

  const handleMouseLeave = () => {
    setHoveredNotification(null);
  };

  const handleRedirect = (redirectUrl: string) => {
    // Fire-and-forget: closing the modal below is deliberately immediate and does not wait on the
    // route transition.
    void navigate(redirectUrl);
    closeNotificationModal();
  };

  const notificationClicked = (notification: NotificationEntry) => {
    if (notification.redirectUrl) {
      if (notification.read === "NOTIFICATION_UNREAD") {
        dismissNotification(notification.id);
      }
      handleRedirect(notification.redirectUrl);
    }
  };

  const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>, notificationId: ObjectIdString) => {
    e.stopPropagation();
    dismissNotification(notificationId);
  };

  return (
    <div className="w-full flex flex-col gap-4 p-4">
      {bucketedGroups.map(({ bucket, items }) => {
        const groupIds = items.map((notification: NotificationEntry) => notification.id);
        return (
          <div key={bucket.key} className="flex flex-col">
            <div className="sticky top-4 z-10 flex h-14 items-center justify-between gap-4 rounded-t-lg border border-[#c6d8ec] bg-white px-6 py-2">
              <Text className="text-base font-semibold text-[#192048] whitespace-nowrap">{bucket.label}</Text>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkGroupRead(bucket.key, groupIds);
                }}
                className="shrink-0 h-8 px-4 rounded-xs bg-[#e8f1fc] text-sm font-semibold text-[#192048] cursor-pointer transition hover:brightness-95"
              >
                {t("Mark as read")}
              </button>
            </div>
            <div className="border-x border-b border-[#c6d8ec] rounded-b-lg bg-white">
              <div className="overflow-hidden rounded-b-lg">
                {items.map((notification: NotificationEntry, index: number) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    layout={layout}
                    t={t}
                    iconTypeMap={iconTypeMap}
                    isFirst={index === 0}
                    isHovered={hoveredNotification === notification.id}
                    isSelected={selectedIds.has(notification.id)}
                    onMouseEnter={() => handleMouseEnter(notification)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => notificationClicked(notification)}
                    onToggleSelect={onToggleSelect}
                    onDismiss={(e) => handleDismiss(e, notification.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default NotificationCard;
